import { Box, HStack, Icon, Menu, Text } from '@chakra-ui/react'
import { Check } from 'lucide-react'
import {
  type ChangeEvent,
  type ComponentProps,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { FilterMenuBase, FilterMenuSection } from './FilterMenuBase'
import { FilterMenuInput } from './FilterMenuInput'
import {
  FIRST_CHAR_SEARCH_SCOPE,
  MAX_INITIAL_OPTIONS,
  MAX_SEARCH_SCOPE_INCLUDES,
  MAX_SEARCH_SCOPE_STARTSWITH,
  MAX_VISIBLE_OPTIONS,
  MEDIUM_DATASET_THRESHOLD,
  MEDIUM_INPUT_THRESHOLD,
  SHORT_DEBOUNCE_TIME,
  SHORT_INPUT_THRESHOLD,
  SMALL_DATASET_THRESHOLD,
  STANDARD_DEBOUNCE_TIME,
} from './constants'

export type FilterOption<T = string> = {
  value: T
  label: string
}

interface FilterMenuProps<T = string>
  extends Omit<
    ComponentProps<typeof FilterMenuBase>,
    'children' | 'hasSelectedValue' | 'onMenuClose'
  > {
  /**
   * The array of options to display in the menu
   */
  options: FilterOption<T>[]

  /**
   * The currently selected option
   */
  selectedOption: FilterOption<T> | null

  /**
   * Callback function triggered when an option is selected
   */
  onOptionChange: (option: FilterOption<T>) => void

  /**
   * Maximum height of the menu list in pixels
   * If the list exceeds this height, it will become scrollable
   * @default 500
   */
  maxMenuHeight?: number

  /**
   * Whether the menu should include a search input
   * If true, a search input will be displayed at the top of the menu
   * @default false
   */
  isSearchable?: boolean

  /**
   * Callback function triggered when scrolling reaches the bottom of the menu list
   * Useful for implementing infinite scrolling or loading more data
   */
  onMenuScrollToBottom?: () => void

  /**
   * Custom render function for menu items
   */
  renderMenuItem?: (option: FilterOption<T>, isSelected: boolean) => ReactNode

  /**
   * Whether the menu should automatically close after selecting an option
   * @default true
   */
  autoCloseOnSelect?: boolean

  /**
   * Defines which fields of the FilterOption to search when filtering
   * @default 'label'
   */
  searchFields?: 'label' | 'value' | 'both'
}

/**
 * A reusable filter menu component that displays a dropdown with selectable options.
 * Supports custom rendering of menu items and shows a checkmark for the selected option.
 */
export function FilterMenu<T = string>({
  buttonLabel,
  headerTitle,
  options,
  selectedOption,
  onOptionChange,
  className,
  style,
  size = 'sm',
  maxMenuHeight = 500,
  menuWidth = 300,
  isSearchable,
  onMenuScrollToBottom,
  menuProps,
  renderMenuItem,
  autoCloseOnSelect = true,
  searchFields = 'label',
  ...restProps
}: FilterMenuProps<T>) {
  const { t } = useTranslation()

  // Default rendering of menu items with check marks for selected items
  const defaultRenderMenuItem = (
    option: FilterOption<T>,
    isSelected: boolean,
  ) => (
    <HStack>
      {isSelected ? <Icon as={Check} /> : <Box width="16px" />}
      <Text fontSize={size}>{option.label}</Text>
    </HStack>
  )

  // Use the custom render function or fall back to the default
  const renderItem = renderMenuItem || defaultRenderMenuItem

  // State for filtering
  const [filter, setFilter] = useState('')
  const [debouncedFilter, setDebouncedFilter] = useState('')
  const searchInputRef = useRef<HTMLInputElement>(null)
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)

  // Use debounced filter value to avoid excessive filtering
  useEffect(() => {
    // Skip debouncing for empty or very short inputs
    if (filter === '' || filter.length <= SHORT_INPUT_THRESHOLD) {
      return
    }

    // Clear any existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }

    // Set a new timer - use a shorter debounce for shorter inputs
    const debounceTime =
      filter.length < MEDIUM_INPUT_THRESHOLD
        ? SHORT_DEBOUNCE_TIME
        : STANDARD_DEBOUNCE_TIME

    debounceTimerRef.current = setTimeout(() => {
      setDebouncedFilter(filter)
    }, debounceTime)

    // Cleanup function
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [filter])

  // Filter the options based on the search input - memoize to avoid expensive recalculations
  const filteredOptions = useMemo(() => {
    // If no filter, return the first MAX_INITIAL_OPTIONS items only for very large datasets
    if (!debouncedFilter) {
      return options.length > MEDIUM_DATASET_THRESHOLD
        ? options.slice(0, MAX_INITIAL_OPTIONS)
        : options
    }

    // Helper function to check if an option matches the search filter based on searchFields
    const matchesSearch = (
      option: FilterOption<T>,
      searchText: string,
    ): boolean => {
      const lowerSearchText = searchText.toLowerCase()

      if (searchFields === 'label') {
        return option.label.toLowerCase().includes(lowerSearchText)
      }

      if (searchFields === 'value') {
        return String(option.value).toLowerCase().includes(lowerSearchText)
      }

      // searchFields === 'both'
      return (
        option.label.toLowerCase().includes(lowerSearchText) ||
        String(option.value).toLowerCase().includes(lowerSearchText)
      )
    }

    // Helper function to check if an option's field starts with the search text
    const startsWithSearch = (
      option: FilterOption<T>,
      searchText: string,
    ): boolean => {
      const lowerSearchText = searchText.toLowerCase()

      if (searchFields === 'label') {
        return option.label.toLowerCase().startsWith(lowerSearchText)
      }

      if (searchFields === 'value') {
        return String(option.value).toLowerCase().startsWith(lowerSearchText)
      }

      // searchFields === 'both'
      return (
        option.label.toLowerCase().startsWith(lowerSearchText) ||
        String(option.value).toLowerCase().startsWith(lowerSearchText)
      )
    }

    // Helper function for single character first letter match
    const matchesFirstChar = (
      option: FilterOption<T>,
      searchText: string,
    ): boolean => {
      const lowerSearchText = searchText.toLowerCase()

      if (searchFields === 'label') {
        return option.label.toLowerCase().charAt(0) === lowerSearchText
      }

      if (searchFields === 'value') {
        return String(option.value).toLowerCase().charAt(0) === lowerSearchText
      }

      // searchFields === 'both'
      return (
        option.label.toLowerCase().charAt(0) === lowerSearchText ||
        String(option.value).toLowerCase().charAt(0) === lowerSearchText
      )
    }

    // For large datasets, use a much more aggressive approach
    const lowerFilter = debouncedFilter.toLowerCase()

    // Use a lookup table approach for very large datasets (MEDIUM_DATASET_THRESHOLD+)
    if (options.length > MEDIUM_DATASET_THRESHOLD) {
      // For first character searches, return only matching first characters
      if (lowerFilter.length === 1) {
        // Search first FIRST_CHAR_SEARCH_SCOPE items for the single character
        return options
          .slice(0, FIRST_CHAR_SEARCH_SCOPE)
          .filter((option) => matchesFirstChar(option, lowerFilter))
      }

      // For 2+ character searches, use startsWith and limit results
      const matches: FilterOption<T>[] = []
      let matchCount = 0

      // First pass: Exact prefix match (most relevant)
      for (
        let i = 0;
        i < Math.min(options.length, MAX_SEARCH_SCOPE_STARTSWITH);
        i++
      ) {
        const option = options[i]
        if (startsWithSearch(option, lowerFilter)) {
          matches.push(option)
          matchCount++
          if (matchCount >= MAX_VISIBLE_OPTIONS) {
            break
          }
        }
      }

      // If we don't have enough exact matches, try includes
      if (matchCount < MAX_VISIBLE_OPTIONS) {
        for (
          let i = 0;
          i < Math.min(options.length, MAX_SEARCH_SCOPE_INCLUDES);
          i++
        ) {
          const option = options[i]
          if (
            !startsWithSearch(option, lowerFilter) &&
            matchesSearch(option, lowerFilter)
          ) {
            matches.push(option)
            matchCount++
            if (matchCount >= MAX_VISIBLE_OPTIONS) {
              break
            }
          }
        }
      }

      return matches
    }

    // For medium datasets, use startsWith for better performance
    if (options.length > SMALL_DATASET_THRESHOLD) {
      // First try startsWith for better performance
      const startsWithMatches = options.filter((option) =>
        startsWithSearch(option, lowerFilter),
      )

      // If we get some matches with startsWith, return those
      if (startsWithMatches.length > 0) {
        return startsWithMatches
      }

      // Otherwise fall back to includes
      return options.filter((option) => matchesSearch(option, lowerFilter))
    }

    // For small datasets, use includes
    return options.filter((option) => matchesSearch(option, lowerFilter))
  }, [debouncedFilter, options, searchFields])

  // Focus the search input when the menu opens
  const handleMenuOpen = useCallback(() => {
    if (isSearchable && searchInputRef.current) {
      // Use a small timeout to ensure the menu is fully rendered
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus()
        }
      }, 10)
    }
  }, [isSearchable])

  // Keep focus on search input during filtering
  const handleFilterInputBlur = useCallback(() => {
    // Refocus if still searchable and input exists
    if (isSearchable && searchInputRef.current) {
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus()
        }
      }, 0)
    }
  }, [isSearchable])

  // Clear filters when menu closes
  const handleMenuClose = useCallback(() => {
    setFilter('')
    setDebouncedFilter('')

    // Also clear any pending debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
      debounceTimerRef.current = null
    }
  }, [])

  // Handle filter input changes
  const handleFilterChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value.trim()

      // Update the filter immediately for UI responsiveness
      setFilter(value)

      // If it's a short input (1-2 chars) or a clear operation, update debounced filter immediately
      // This gives faster feedback for short inputs that are common in searches
      if (value === '' || value.length <= SHORT_INPUT_THRESHOLD) {
        setDebouncedFilter(value)

        // Clear any pending timer
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current)
          debounceTimerRef.current = null
        }
      }
    },
    [],
  )

  // Handle option selection
  const handleOptionClick = useCallback(
    (option: FilterOption<T>) => {
      onOptionChange(option)
    },
    [onOptionChange],
  )

  // Render search input section if searchable
  const renderSearchSection = () => {
    if (!isSearchable) {
      return null
    }

    return (
      <FilterMenuSection>
        <FilterMenuInput
          value={filter}
          onChange={handleFilterChange}
          placeholder={t('components.filterMenu.searchPlaceholder') as string}
          inputRef={searchInputRef}
          onClear={() => {
            setFilter('')
            setDebouncedFilter('')
          }}
          onBlur={handleFilterInputBlur}
          onKeyDown={(e) => {
            e.stopPropagation()
            // Close the menu on Escape, but clear the filter first
            if (e.key === 'Escape') {
              setFilter('')
              setDebouncedFilter('')
            }
          }}
        />
      </FilterMenuSection>
    )
  }

  // Render options section
  const renderOptionsSection = (closeMenu?: () => void) => {
    // Show empty state if no options
    if (filteredOptions.length === 0) {
      return (
        <Box px={3} py={2} textAlign="center" color="gray.500">
          <Text fontSize={size}>
            {t('components.filterMenu.noMatchingOptions')}
          </Text>
        </Box>
      )
    }

    return (
      <Box
        maxH={`${maxMenuHeight}px`}
        overflowY="auto"
        overflowX="hidden"
        onScroll={(e) => {
          // Only trigger scroll callback if we're not actively filtering
          if (onMenuScrollToBottom && !filter) {
            const target = e.target as HTMLDivElement
            const isAtBottom =
              Math.abs(
                target.scrollHeight - target.scrollTop - target.clientHeight,
              ) < 1

            if (isAtBottom) {
              onMenuScrollToBottom()
            }
          }
        }}
      >
        {/* Limit rendering to a reasonable number of items */}
        {filteredOptions
          .slice(0, MAX_VISIBLE_OPTIONS)
          .map((option: FilterOption<T>, index: number) => {
            const isLast =
              index ===
              Math.min(filteredOptions.length, MAX_VISIBLE_OPTIONS) - 1
            return (
              <Menu.Item
                key={String(option.value)}
                value={String(option.value)}
                onClick={() => {
                  handleOptionClick(option)
                  if (autoCloseOnSelect && closeMenu) {
                    handleMenuClose() // Clear filters
                    closeMenu() // Close the menu
                  }
                }}
                borderBottom={isLast ? 'none' : '1px solid'}
                borderColor="gray.200"
                _hover={{
                  backgroundColor: 'var(--chakra-colors-shell-muted)',
                }}
              >
                {renderItem(option, selectedOption?.value === option.value)}
              </Menu.Item>
            )
          })}

        {/* Show a message when there are more items */}
        {filteredOptions.length > MAX_VISIBLE_OPTIONS && (
          <Box
            px={3}
            py={2}
            textAlign="center"
            color="gray.500"
            borderTop="1px solid"
            borderColor="gray.200"
          >
            <Text fontSize={size}>
              {t('components.filterMenu.showingItems', {
                showing: MAX_VISIBLE_OPTIONS,
                total: filteredOptions.length,
              })}
              {!filter && t('components.filterMenu.useSearchHint')}
            </Text>
          </Box>
        )}
      </Box>
    )
  }

  return (
    <FilterMenuBase
      buttonLabel={buttonLabel}
      headerTitle={headerTitle}
      className={className}
      style={style}
      size={size}
      menuWidth={menuWidth}
      hasSelectedValue={!!selectedOption}
      menuProps={menuProps}
      onMenuOpen={handleMenuOpen}
      onMenuClose={handleMenuClose}
      {...restProps}
    >
      {({ closeMenu }) => (
        <>
          {renderSearchSection()}
          {renderOptionsSection(closeMenu)}
        </>
      )}
    </FilterMenuBase>
  )
}
